import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'components/PageTitle';
import Container from 'react-bootstrap/esm/Container';
import Row from 'react-bootstrap/esm/Row';
import Col from 'react-bootstrap/esm/Col';
import { LeagueName } from '../../consts';
import ListGroup from 'react-bootstrap/esm/ListGroup';
import { useGetLeaguesQuery, useLazyGetLeagueTeamsQuery } from 'app/api';
import Loader from 'components/Loader/Loader.style';
import SvgIcon from 'components/SvgIcon';
import UserIcon from '../../../assets/Icon/user.svg';
import { remToPx } from 'utils/utils';
import { BLACK } from 'styles/colors';
import { ILeague } from 'types/types';

const MetaTitle = `Drużyny - ${LeagueName}`;
const MetaDescription =
  'W lidze wezmą udział drużyny z dwóch warszawskich hal wspinaczkowych CAMP4 i MAKAK';

const Teams: React.FC = () => {
  const { data: leagues, isLoading: leaguesLoading } = useGetLeaguesQuery();
  const [fetchTeams] = useLazyGetLeagueTeamsQuery();
  const [leaguesWithTeams, setLeaguesWithTeams] = useState<ILeague[]>([]);

  useEffect(() => {
    if (!leagues) return;

    setLeaguesWithTeams(
      leagues.map((league) => ({
        ...league,
        teams: [],
        isLoading: true,
      })),
    );

    leagues.forEach(async (league) => {
      try {
        const teams = await fetchTeams({ league_id: league.id }).unwrap();
        setLeaguesWithTeams((prev) =>
          prev.map((l) =>
            l.id === league.id ? { ...l, teams, isLoading: false } : l,
          ),
        );
      } catch (error) {
        setLeaguesWithTeams((prev) =>
          prev.map((l) =>
            l.id === league.id ? { ...l, teams: [], isLoading: false } : l,
          ),
        );
      }
    });
  }, [leagues, fetchTeams]);

  return (
    <>
      <Helmet>
        <title>{MetaTitle}</title>
        <meta name="description" content={MetaDescription} />
        <meta property="og:title" content={MetaTitle} />
        <meta property="og:description" content={MetaDescription} />
        <meta name="twitter:title" content={MetaTitle} />
        <meta name="twitter:description" content={MetaDescription} />
      </Helmet>
      <section>
        <PageTitle>Drużyny</PageTitle>
        <Container className="pt-5 pb-5">
          <Row className="justify-content-center mb-5">
            <Col xs={12} md={6}>
              <p className="text-center">
                W lidze biorą udział drużyny z&nbsp;dwóch warszawskich hal
                wspinaczkowych CAMP4&nbsp;i&nbsp;MAKAK
              </p>
            </Col>
          </Row>
          {leaguesLoading && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '10rem' }}
            >
              <Loader color={BLACK} size={10} />
            </div>
          )}
          {leaguesWithTeams.map((league) => (
            <div key={league.id}>
              <Row className="justify-content-center">
                <Col xs={12}>
                  <h2 className="text-center">
                    {league?.attrs.ageCategory}
                    {league?.attrs.ageCategoryDescription &&
                      ` (${league?.attrs.ageCategoryDescription})`}
                  </h2>
                  {league.isLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: '10rem' }}
                    >
                      <Loader color={BLACK} size={10} />
                    </div>
                  )}
                </Col>
              </Row>
              {!league.isLoading && (
                <Row className="justify-content-center mb-5">
                  {league.teams?.map((team) => (
                    <Col
                      xs={12}
                      sm={6}
                      md={5}
                      lg={4}
                      xl={3}
                      key={`${league.id}-${team.id}`}
                    >
                      <h3 className="text-center">{team.name}</h3>
                      <ListGroup className="mb-3 w-100">
                        {team.players.map((player) => (
                          <ListGroup.Item
                            key={`${team.id}-${player.nickname}`}
                            className="text-center"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'start',
                              columnGap: '1rem',
                            }}
                          >
                            <div
                              className="rounded"
                              style={{
                                backgroundColor: team.attrs.colors?.primary,
                                width: remToPx(4),
                                height: remToPx(4),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {player.avatar ? (
                                <img
                                  src={player.avatar}
                                  width={remToPx(4)}
                                  height={remToPx(4)}
                                  className="rounded"
                                />
                              ) : (
                                <SvgIcon
                                  src={UserIcon}
                                  width="2.5rem"
                                  height="2.5rem"
                                />
                              )}
                            </div>
                            {player.name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          ))}
        </Container>
      </section>
    </>
  );
};

export default Teams;
